import { create } from 'zustand';
import { RequestStatus } from '@/shared/model/requestStatus';
import { userChatProfileService } from '../api/userChatProfileService';
import { appChatService } from '@/entities/AppChat/api/appChatService';
import { mapAppChatActivity, sortUserChatProfiles } from '../mappers';
import type { UserChatDirectoryActions, UserChatDirectoryState } from './types';

export const USER_CHAT_DIRECTORY_PAGE_SIZE = 50;

const initialState: UserChatDirectoryState = {
    profiles: [],
    allProfiles: [],
    pageSize: USER_CHAT_DIRECTORY_PAGE_SIZE,
    totalCount: 0,
    role: null,
    page: 0,
    status: RequestStatus.Idle,
    error: null,
    activity: {},
};

let requestRevision = 0;

/** Хранит пользователей по свежести переписки независимо от открытого диалога */
export const useUserChatDirectory = create<UserChatDirectoryState & UserChatDirectoryActions>((
    set,
    get
) => ({
    ...initialState,

    setPageSize: size => {
        const pageSize = Math.max(1, size);

        set({
            pageSize,
            page: 0,
            profiles: get().allProfiles.slice(0, pageSize)
        });
    },

    load: async (role, requestedPage = 0, refresh = false) => {
        if (!refresh && get().role === role && get().status === RequestStatus.Success) {
            const pageSize = get().pageSize;
            const page = Math.min(requestedPage, Math.max(0, Math.ceil(get().totalCount / pageSize) - 1));

            set({
                page,
                profiles: get().allProfiles.slice(page * pageSize, (page + 1) * pageSize)
            });

            return;
        }

        const revision = ++requestRevision;
        const sameRole = get().role === role;

        set({
            ...(sameRole
                ? {}
                : initialState),
            role,
            status: RequestStatus.Loading,
            error: null,
        });

        try {
            const [profiles, documents] = await Promise.all([
                userChatProfileService.getDirectoryProfiles(role),
                appChatService.getActivity(),
            ]);

            if (revision !== requestRevision) return;

            const activity = mapAppChatActivity(documents);

            const allProfiles = sortUserChatProfiles(profiles, activity);
            const pageSize = get().pageSize;
            const page = Math.min(requestedPage, Math.max(0, Math.ceil(allProfiles.length / pageSize) - 1));

            set({
                allProfiles,
                profiles: allProfiles.slice(page * pageSize, (page + 1) * pageSize),
                totalCount: allProfiles.length,
                page,
                activity,
                status: RequestStatus.Success,
            });
        } catch {
            if (revision !== requestRevision) return;

            set({
                status: RequestStatus.Error,
                error: 'Не удалось загрузить пользователей и активность чатов'
            });
        }
    },

    reset: () => {
        requestRevision += 1;
        set(initialState);
    },
}));
